import {
  DIARY_CATEGORIES,
  type DiaryCategory,
  type DiaryEntry,
  type DiaryPrompt,
} from "@couples/shared";
import { ImagePlus, X } from "lucide-react";
import { useMemo, useState } from "react";

import type { EntryInput } from "../lib/diary/entries";
import { useMediaThumbnails } from "../lib/media/useMediaThumbnails";
import MediaLinkPicker from "./MediaLinkPicker";
import SheetShell from "./SheetShell";

interface FormState {
  category: DiaryCategory | "";
  promptId: string;
  promptAnswer: string;
  body: string;
}

const INITIAL_STATE: FormState = { category: "", promptId: "", promptAnswer: "", body: "" };

function initialStateOf(entry: DiaryEntry, prompts: DiaryPrompt[]): FormState {
  const prompt = entry.promptId !== null ? prompts.find((p) => p.id === entry.promptId) : undefined;
  return {
    category: prompt?.category ?? "",
    promptId: entry.promptId ?? "",
    promptAnswer: entry.promptAnswer ?? "",
    body: entry.body ?? "",
  };
}

interface Props {
  prompts: DiaryPrompt[];
  /** 既存のエントリーを直すときだけ渡す。無ければ新規作成 */
  editingEntry?: DiaryEntry;
  /** 既に結び付けてあるメディアID(直すとき) */
  initialMediaIds?: string[];
  busy: boolean;
  onSave: (input: EntryInput, mediaIds: string[]) => void;
  onClose: () => void;
}

/** 「日記」欄と「お題に答える」欄を持つ1つのエントリーを作る・直す */
export default function DiaryEntrySheet({
  prompts,
  editingEntry,
  initialMediaIds = [],
  busy,
  onSave,
  onClose,
}: Props) {
  const isEditing = editingEntry !== undefined;
  const [form, setForm] = useState<FormState>(() =>
    editingEntry !== undefined ? initialStateOf(editingEntry, prompts) : INITIAL_STATE,
  );
  const [mediaIds, setMediaIds] = useState<string[]>(initialMediaIds);
  const [picking, setPicking] = useState(false);

  function set(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  // 選んだものだけのサムネイル。アルバム側で消されていれば URL は引けず、枠だけ残る
  const { urlOf } = useMediaThumbnails(mediaIds);

  function removeMedia(mediaId: string) {
    setMediaIds((current) => current.filter((id) => id !== mediaId));
  }

  const promptsInCategory = useMemo(
    () => prompts.filter((prompt) => prompt.category === form.category),
    [prompts, form.category],
  );

  const bodyText = form.body.trim();
  const answerText = form.promptAnswer.trim();
  const hasPrompt = form.promptId !== "";
  // お題を選んだなら回答が要る。選んでいなければ日記だけでもよい
  const answerMissing = hasPrompt && answerText === "";
  const hasContent = bodyText !== "" || (hasPrompt && answerText !== "");
  const canSubmit = hasContent && !answerMissing;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    onSave(
      {
        promptId: hasPrompt ? form.promptId : null,
        promptAnswer: hasPrompt ? answerText : null,
        body: bodyText === "" ? null : bodyText,
      },
      mediaIds,
    );
  }

  return (
    <SheetShell
      title={isEditing ? "日記を直す" : "日記を書く"}
      onClose={onClose}
      elevated
      tone="bg"
    >
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-4">
          <div className="rounded-xl border border-[var(--color-line)] bg-white p-3">
            <span className="mb-1.5 block text-[11px] font-semibold text-[var(--color-sub)]">
              お題に答える(任意)
            </span>
            <div className="flex gap-2">
              <select
                value={form.category}
                onChange={(event) => {
                  set({
                    category: event.target.value as DiaryCategory | "",
                    promptId: "",
                    promptAnswer: "",
                  });
                }}
                className="flex-1 rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-sm outline-none"
              >
                <option value="">カテゴリを選ばない</option>
                {DIARY_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {form.category !== "" && (
              <select
                value={form.promptId}
                onChange={(event) => {
                  set({ promptId: event.target.value });
                }}
                className="mt-2 w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-sm outline-none"
              >
                <option value="">お題を選ぶ</option>
                {promptsInCategory.map((prompt) => (
                  <option key={prompt.id} value={prompt.id}>
                    {prompt.body}
                  </option>
                ))}
              </select>
            )}

            {hasPrompt && (
              <textarea
                autoFocus
                value={form.promptAnswer}
                onChange={(event) => {
                  set({ promptAnswer: event.target.value });
                }}
                placeholder="お題への回答"
                rows={3}
                className="mt-2 w-full resize-none rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
              />
            )}
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">日記(任意)</span>
            <textarea
              value={form.body}
              onChange={(event) => {
                set({ body: event.target.value });
              }}
              placeholder="今日あったことを書く"
              rows={5}
              className="w-full resize-none rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
            />
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] text-[var(--color-sub)]">アルバムの写真・動画(任意)</span>
            <div className="flex flex-wrap gap-2">
              {mediaIds.map((mediaId) => {
                const url = urlOf(mediaId);
                return (
                  <div
                    key={mediaId}
                    className="relative size-16 overflow-hidden rounded-md bg-[var(--color-line)]"
                  >
                    {url !== undefined && (
                      <img src={url} alt="" loading="lazy" className="size-full object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        removeMedia(mediaId);
                      }}
                      aria-label="この写真・動画を外す"
                      className="absolute top-0.5 right-0.5 flex size-4 items-center justify-center rounded-full bg-black/55 text-white"
                    >
                      <X className="size-2.5" aria-hidden />
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  setPicking(true);
                }}
                aria-label="アルバムから選ぶ"
                className="flex size-16 items-center justify-center rounded-md border border-dashed border-[var(--color-line)] text-[var(--color-sub)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
              >
                <ImagePlus className="size-5" aria-hidden />
              </button>
            </div>
            {/* 本文が空だと保存できないため、写真だけ選んで止まらないよう理由を添える */}
            {mediaIds.length > 0 && !hasContent && (
              <p className="text-[11px] text-[var(--color-warn)]">
                日記かお題への回答も書いてください。
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2.5 border-t border-[var(--color-line)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="submit"
            disabled={busy || !canSubmit}
            className="flex-1 rounded-xl bg-[var(--color-teal)] py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {isEditing ? "直す" : "書く"}
          </button>
        </div>
      </form>

      {picking && (
        <MediaLinkPicker
          initialSelected={mediaIds}
          onDone={(ids) => {
            setMediaIds(ids);
            setPicking(false);
          }}
          onClose={() => {
            setPicking(false);
          }}
        />
      )}
    </SheetShell>
  );
}
